import api from './api';

export type Match = { id: number; home?: string; away?: string; date?: string; score?: string };
export type Standing = { team: string; played: number; won: number; draw: number; lost: number; points: number };
export type Player = {
  id: number;
  first_name: string;
  last_name: string;
  position?: string;
  jersey_number?: number;
  image_url?: string;
  gender?: string;
  is_active?: boolean;
  // Extended detail fields (optional on public endpoint)
  nationality?: string;
  date_of_birth?: string;
  height?: number;
  weight?: number;
  email?: string;
  phone?: string;
  team_id?: number;
  team?: { id?: number; name?: string };
};
export type Sponsor = { id: number; name: string; logo_url?: string; website_url?: string; tier?: string; display_order?: number };
export type Category = { id?: number; name: string; slug?: string; url?: string; children?: Category[] };

function normalizePlayer(p: any): Player {
  if (!p) return p as any;
  const id = p.id ?? p.ID;
  return {
    id: typeof id === 'string' ? Number(id) : id,
    first_name: p.first_name ?? p.FirstName ?? '',
    last_name: p.last_name ?? p.LastName ?? '',
    position: p.position ?? p.Position ?? undefined,
    jersey_number: p.jersey_number ?? p.JerseyNumber ?? undefined,
    image_url: p.image_url ?? p.ImageURL ?? undefined,
    gender: p.gender ?? p.Gender ?? undefined,
    is_active: Boolean(p.is_active ?? p.IsActive ?? true),
    nationality: p.nationality ?? p.Nationality ?? undefined,
    date_of_birth: p.date_of_birth ?? p.DateOfBirth ?? undefined,
    height: p.height ?? p.Height ?? undefined,
    weight: p.weight ?? p.Weight ?? undefined,
    email: p.email ?? p.Email ?? undefined,
    phone: p.phone ?? p.Phone ?? undefined,
    team_id: p.team_id ?? p.TeamID ?? (p.team?.id ?? p.Team?.ID) ?? undefined,
    team: (p.team || p.Team) ? { id: (p.team?.id ?? p.Team?.ID), name: (p.team?.name ?? p.Team?.Name ?? p.Team?.name) } : undefined,
  } as Player;
}

export async function getMatches() {
  const res = await api.get<Match[] | { data: Match[] }>('/matches');
  return Array.isArray(res.data) ? res.data : res.data.data;
}

export async function getStandings() {
  const res = await api.get<Standing[] | { data: Standing[] }>('/standings');
  return Array.isArray(res.data) ? res.data : res.data.data;
}

export async function getPlayers(opts?: { active?: boolean; team_id?: number | string }) {
  let url = '/players';
  const params = new URLSearchParams();
  if (opts && opts.active === false) params.set('active', 'false');
  if (opts && opts.team_id != null) params.set('team_id', String(opts.team_id));
  if (Array.from(params.keys()).length > 0) url += `?${params.toString()}`;
  const res = await api.get<any[] | { data?: any[]; items?: any[] }>(url);
  const raw = Array.isArray(res.data)
    ? res.data
    : ((res.data as any).data || (res.data as any).items);
  return (raw || []).map(normalizePlayer);
}

export async function getPlayer(id: number | string) {
  const res = await api.get<any>(`/players/${id}`);
  return normalizePlayer(res.data);
}

export async function getSponsors() {
  const res = await api.get<Sponsor[] | { data: Sponsor[] }>('/sponsors');
  return Array.isArray(res.data) ? res.data : res.data.data;
}

export async function sendContact(payload: { name: string; email: string; subject: string; message: string; source?: string }) {
  const res = await api.post('/contact', payload);
  return res.data;
}

export async function subscribeToNewsletter(email: string, preferences?: Record<string, boolean> ) {
  const payload: any = { email };
  try {
    // Provide backend with the correct site origin for preference/unsubscribe links
    const origin = window?.location?.origin;
    if (origin) (payload as any).site_base_url = origin;
  } catch {}
  if (preferences) payload.preferences = preferences;
  const res = await api.post('/newsletter/subscribe', payload);
  return res.data;
}

export async function getCategories() {
  const res = await api.get<Category[] | { data: Category[] }>('/categories');
  return Array.isArray(res.data) ? res.data : res.data.data;
}
